import {TNode} from 'ghostban';
import _, {compact} from 'lodash';

export const range = (start: number, end: number) =>
    Array.from(Array(Math.abs(end - start) + 1), (_, i) => start + i);

export const formatCappedCount = (
    value: number,
    max: number
): string | number => (value > max ? `${max}+` : value);

type Options = {
    deep?: boolean;
};

type PlainObject = {[key: string]: any};

export const camelcaseKeys = (
    obj: any,
    options: Options = {deep: false}
): any => {
    const shouldDeepTransform = options.deep ?? false;

    if (Array.isArray(obj)) {
        return obj.map(item =>
            shouldDeepTransform ? camelcaseKeys(item, options) : item
        );
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((result: PlainObject, key: string) => {
            const camelKey = _.camelCase(key);
            const value = obj[key];

            result[camelKey] =
                shouldDeepTransform &&
                typeof value === 'object' &&
                value !== null
                    ? camelcaseKeys(value, options)
                    : value;

            return result;
        }, {});
    }

    return obj;
};

export const snakecaseKeys = (
    obj: any,
    options: Options = {deep: false}
): any => {
    const shouldDeepTransform = options.deep ?? false;

    if (Array.isArray(obj)) {
        return obj.map(item =>
            shouldDeepTransform ? snakecaseKeys(item, options) : item
        );
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((result: PlainObject, key: string) => {
            const snakeKey = _.snakeCase(key);
            const value = obj[key];

            result[snakeKey] =
                shouldDeepTransform &&
                typeof value === 'object' &&
                value !== null
                    ? snakecaseKeys(value, options)
                    : value;

            return result;
        }, {});
    }

    return obj;
};

export const convertSgfPathToCompressedPath = (sgfPath: string) => {
    return (
        sgfPath
            .match(/\[([a-z]{2})\]/g)
            ?.map((match: string) => match[1].toUpperCase() + match[2])
            .join('') ?? ''
    );
};

export const getCompressedPathFromNode = (node: TNode) => {
    const path = compact(
        node.getPath().map(n => n.model.moveProps[0]?.toString())
    ).join(';');
    return convertSgfPathToCompressedPath(path);
};

export const uncompressPath = (path: string) => {
    if (path.includes('.')) {
        return path;
    }

    let s = '';
    for (let i = 0; i < path.length / 2; i++) {
        const x1 = path.charCodeAt(i * 2) - 'A'.charCodeAt(0);
        const x2 = path.charCodeAt(i * 2 + 1) - 'a'.charCodeAt(0);

        if (i > 0) {
            s += '|';
        }
        s += `${x1}.${x2}`;
    }
    return s;
};

export const convertPathToTurns = (path: string) => {
    const pa = path.split('|');
    let turns = '';

    for (const turn of pa) {
        const [x, y] = turn.split('.');
        let n = parseInt(x);

        if (n >= 8) {
            n++; // skip i
        }

        if (y !== undefined) {
            turns +=
                ' ' +
                String.fromCharCode('A'.charCodeAt(0) + n) +
                (19 - parseInt(y));
        }
    }

    return turns;
};

export function lastArrayItem<T>(arr: T[]): T {
    return arr[arr.length - 1];
}
